import { TDestinationCredentials } from '@/core/apis/config_api/config_api_types';
import { TMableEvent } from '@/core/constants/event_types/base_mable_event';
import { EMableEventName } from '@/core/constants/event_types/events';
import { TimeUtils } from '@/core/event_logic/utils/time_utils';
import { DestEventBuilder } from '@/destinations/shared/destination_event_builder/destination_event_builder';
import { TMetaDestinationCredentials } from '@/destinations/types/destination_creds_types';

import { FbCustomData, FbEventName, FbUserData } from './types';
import { normalizeAndHashUserData } from './user_data_normalizer';

export function mapMableEventToFbEvent(
  mableEvent: EMableEventName,
  esd?: { cen?: string },
): string {
  if (mableEvent === EMableEventName.CUSTOM_EVENT && esd?.cen) {
    return esd?.cen;
  }

  const eventMappings: Partial<Record<EMableEventName, string>> = {
    [EMableEventName.CHECKOUT_STARTED]: FbEventName.INITIATE_CHECKOUT,
    [EMableEventName.LEAD]: FbEventName.LEAD,
    [EMableEventName.ORDER_COMPLETED]: FbEventName.PURCHASE,
    [EMableEventName.PAGE_VIEWED]: FbEventName.PAGE_VIEW,
    [EMableEventName.PAYMENT_INFO_ADDED]: FbEventName.ADD_PAYMENT_INFO,
    [EMableEventName.POST_PURCHASE_SURVEY]: FbEventName.CUSTOM,
    [EMableEventName.PRODUCT_ADDED]: FbEventName.ADD_TO_CART,
    [EMableEventName.PRODUCT_VIEWED]: FbEventName.VIEW_CONTENT,
  };

  return eventMappings[mableEvent] || FbEventName.CUSTOM;
}

export function extractUserData(mableEvent: TMableEvent): FbUserData {
  return {
    country: mableEvent.cdas?.cc || [],
    ct: mableEvent.cdas?.ct || [],
    db: mableEvent.cdas?.dob || [],
    em: mableEvent.cdas?.em || [],
    external_id: [
      mableEvent?.ii,
      mableEvent.sd?.sid,
      mableEvent.bd?.aid,
      ...(mableEvent.cdas?.id || []),
    ].filter(Boolean),
    fn: mableEvent.cdas?.fn || [],
    ge: mableEvent.cdas?.ge || [],
    ln: mableEvent.cdas?.ln || [],
    ph: mableEvent.cdas?.ph || [],
    st: mableEvent.cdas?.rg || [],
    zp: (mableEvent.cdas?.zip || [])?.map(String) || [],
  };
}

export function extractCustomData(
  mableEvent: TMableEvent,
  destinationConfig: TDestinationCredentials,
): FbCustomData {
  const customData: FbCustomData = {};

  if (mableEvent?.esd && mableEvent.en !== EMableEventName.PAGE_VIEWED) {
    customData.currency =
      mableEvent.esd?.act_product?.cur ||
      mableEvent.esd?.cart?.currency?.toUpperCase() ||
      'EUR';

    if (mableEvent.esd?.cv_data?.cv !== undefined) {
      customData.value = Number(mableEvent.esd.cv_data.cv.toFixed(2));
    }

    if (mableEvent.esd?.act_product) {
      customData.content_name = mableEvent.esd?.act_product?.nam || '';
      customData.content_category = mableEvent.esd?.act_product?.cat || '';
      const actProductPrice = Number(mableEvent.esd?.act_product?.prc) || 0;
      const quantity = mableEvent.esd?.act_product?.qty || 0;
      customData.num_items = quantity;

      const discount = mableEvent.esd.act_product.totalDiscount || 0;
      const finalPrice =
        quantity > 0 ? actProductPrice - discount / quantity : actProductPrice;

      if (customData.value === undefined) {
        customData.value = Number((finalPrice * quantity).toFixed(2));
      }

      const act_product_id =
        DestEventBuilder.getProductCatalogItemIdForMableProduct(
          mableEvent.esd?.act_product,
          destinationConfig,
        );
      customData.content_ids = [act_product_id];
      customData.contents = JSON.stringify([
        {
          id: act_product_id,
          item_price: Number(finalPrice.toFixed(2)),
          quantity: quantity,
        },
      ]);
      customData.content_type = 'product';
    }

    if (mableEvent.esd?.cart) {
      if (customData.value === undefined) {
        customData.value =
          Number(Number(mableEvent.esd?.cart?.total?.price).toFixed(2)) || 0;
      }
      customData.currency =
        mableEvent.esd?.cart?.currency?.toUpperCase() ||
        mableEvent.esd?.act_product?.cur?.toUpperCase() ||
        'EUR';
      const cartQuantity =
        mableEvent.esd?.cart?.products
          ?.map(product => product?.qty || 0)
          ?.reduce((acc, curr) => acc + curr, 0) || 0;
      customData.num_items = cartQuantity;
      customData.content_ids =
        mableEvent.esd?.cart?.products?.map(product =>
          DestEventBuilder.getProductCatalogItemIdForMableProduct(
            product,
            destinationConfig,
          ),
        ) || [];
      customData.content_type = 'product';
      customData.contents = JSON.stringify(
        mableEvent.esd?.cart?.products?.map(product => {
          const discount = product.totalDiscount || 0;
          const qty = product.qty || 0;
          const price = Number(product?.prc || '0');

          const finalPrice = qty > 0 ? price - discount / qty : price;

          return {
            id: DestEventBuilder.getProductCatalogItemIdForMableProduct(
              product,
              destinationConfig,
            ),
            item_price: finalPrice || 0,
            quantity: qty,
          };
        }) || [],
      );
    }

    if (mableEvent.en === EMableEventName.PRODUCT_ADDED) {
      const quantity = mableEvent.esd?.act_product?.qty || 1;
      customData.num_items = quantity;
      customData.content_type = 'product';
      if (customData.value === undefined) {
        const pdtDiscount = mableEvent.esd?.act_product?.totalDiscount || 0;
        const price = Number(mableEvent.esd?.act_product?.prc) || 0;
        customData.value = Number((price * quantity - pdtDiscount).toFixed(2));
      }
      customData.content_ids = [
        DestEventBuilder.getProductCatalogItemIdForMableProduct(
          mableEvent.esd?.act_product,
          destinationConfig,
        ),
      ];
    }
  }

  if (
    mableEvent?.en === EMableEventName.ORDER_COMPLETED &&
    mableEvent.esd?.oid
  ) {
    customData.order_id = mableEvent.esd?.oid;
  }

  return customData;
}

/**
 * Generates fbc from fbclid
 * @see https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/fbp-and-fbc/
 * @param fbclid
 * @returns fbc
 */
export async function generateFbcFromFbclid(fbclid: string): Promise<string> {
  if (!fbclid) {
    return '';
  }
  const version = 'fb';
  const subDomainIndex = '1';
  const creationTimeMs = await TimeUtils.getCurrentEpochInMsSynced();
  return `${version}.${subDomainIndex}.${creationTimeMs}.${fbclid}`;
}

export async function extractCookies(mableEvent: TMableEvent): Promise<{
  fbc: string;
  fbp: string;
}> {
  const fbc = await generateFbcFromFbclid(mableEvent.pids?.fbclid?.[0] || '');
  const fbp = mableEvent.bd?.cks?.['_fbp'] || '';

  return {
    fbc,
    fbp,
  };
}

export function extraMetaData(mableEvent: TMableEvent): {
  eventId: string;
  initTimeStamp: number;
  timestamp: number;
} {
  return {
    eventId: mableEvent?.eid || '',
    initTimeStamp: mableEvent.sd?.ssts || 0,
    timestamp: mableEvent?.ts || 0,
  };
}

export async function transformMableEventToFbEvent(
  mableEvent: TMableEvent,
  destinationConfig: TMetaDestinationCredentials,
): Promise<URLSearchParams> {
  let eventName;
  if (mableEvent.en === EMableEventName.CUSTOM_EVENT && mableEvent.esd?.cen) {
    eventName = mableEvent.esd.cen;
  } else {
    eventName = mapMableEventToFbEvent(mableEvent.en);
  }
  const userData = extractUserData(mableEvent);
  const hashedAndNormalizedUserData = await normalizeAndHashUserData(userData);
  const customData = extractCustomData(mableEvent, destinationConfig);
  const { fbc, fbp } = await extractCookies(mableEvent);
  const { eventId, initTimeStamp, timestamp } = extraMetaData(mableEvent);

  const params = new URLSearchParams({
    a: 'wtag',
    cdl: 'API_unavailable',
    coo: 'false',
    cs_est: 'true',
    dl: mableEvent.pd?.dl || '',
    dpo: '',
    ec: '0',
    eid: eventId,
    ev: eventName,
    fbc: fbc || '',
    fbp: fbp || '',
    id: destinationConfig.pixelId || '', // Assuming this is the pixel ID
    if: 'false',
    it: initTimeStamp.toString(),
    ler: 'empty',
    o: '4126',
    r: 'stable',
    rl: mableEvent.pd?.rl || '',
    rqm: 'GET',
    sh: mableEvent.bd?.srh?.toString(),
    sw: mableEvent.bd?.srw?.toString(),
    ts: timestamp.toString(),
    v: '2.9.164',
  });

  // Add user data with empty string fallback
  Object.entries(hashedAndNormalizedUserData).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      params.append(`ud[${key}]`, value.length > 0 ? value.join(',') : '');
    } else if (typeof value === 'string') {
      params.append(`ud[${key}]`, value || '');
    }
  });

  // Add custom data
  Object.entries(customData).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(`cd[${key}]`, String(value));
    }
  });

  return params;
}
